import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {cloneDeep, merge} from 'lodash';

import {AcNavAutoService} from '../ac-nav-auto/ac-nav-auto.service';
import {SessionStorageService} from '../../../services/session-storage.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

export class NavItem {
    url: string;
    text?: string;
    icon?: string;
    visible?: boolean;
    isLoading? = false;
    active?: boolean;
    resolverReturn?: any;

    onRemove?: () => null;

    constructor(url, text, icon?) {
        this.url = url;
        this.text = text;
        this.icon = icon;
    }
}

@UntilDestroy()
@Injectable({providedIn: 'root'})
export class DynamicTabsService {
    anyHidden = false;
    anyOfHiddenActive = false;
    private _dynamicTabs: BehaviorSubject<NavItem[]> = new BehaviorSubject<NavItem[]>([]);
    dynamicTabs$: Observable<NavItem[]> = this._dynamicTabs.asObservable();

    constructor(private acNavAutoService: AcNavAutoService) {
        this._dynamicTabs.next(SessionStorageService.getData('dynamicTabs') || []);
        this.dynamicTabs$.pipe(untilDestroyed(this)).subscribe(() => {
            this.updateSessionStorage();
        });
    }

    get currentTabs() {
        return this._dynamicTabs.value;
    }

    getByUrl(url: string): NavItem {
        return this.currentTabs.find((tab) => url.startsWith(tab.url));
    }

    addTab(url: string, text?: string, icon?: string) {
        if (!this._dynamicTabs.value.some(x => x.url === url)) {
            const newArr =this._dynamicTabs.value
            newArr.push(new NavItem(url, text, icon));
            this._dynamicTabs.next(newArr);
            this.updateSessionStorage();
        }
    }

    updateTab(navItem: NavItem) {
        const tab = this.getByUrl(navItem.url);
        if (!tab) {
            return;
        }

        delete navItem.url;
        merge(tab, navItem);
        this.updateSessionStorage();
    }

    removeTab(dynTab: NavItem, isActive = false) {
        if (isActive) {
            this.acNavAutoService.gotoLastStaticState(true);
        }
        this.acNavAutoService.removeRoute(dynTab.url);
        const index = this._dynamicTabs.value.indexOf(dynTab);
        if (index > -1) {
            dynTab.onRemove && dynTab.onRemove();
            this._dynamicTabs.value.splice(index, 1);
            this._dynamicTabs.next(this._dynamicTabs.value);
        }
    }

    setVisible(i: number, isVisible: boolean) {
        const currentIsVisible = this._dynamicTabs.value[Object.getOwnPropertyNames(this._dynamicTabs.value)[i]]?.visible;
        if (this._dynamicTabs.value.length > 0 && currentIsVisible !== isVisible) {
            this._dynamicTabs.value[Object.getOwnPropertyNames(this._dynamicTabs.value)[i]].visible = isVisible;
        }
    }

    updateSessionStorage() {
        this.updateHiddenTabsActive();

        const tabs = cloneDeep(this._dynamicTabs.value);
        tabs.forEach((tab: NavItem) => {
            tab.isLoading = false;
            delete tab.resolverReturn;
            delete tab.onRemove;
        });
        SessionStorageService.setData('dynamicTabs', tabs);
    }

    clear() {
        this._dynamicTabs.next([]);
    }

    updateHiddenTabsActive() {
        this.anyOfHiddenActive = false;
        this.currentTabs.forEach((d, index) => {
            this.anyOfHiddenActive = this.anyOfHiddenActive || (!d.visible && d.active);
        });
    }
}
